import React, { useEffect, useState } from 'react';
import { searchSkuByPartNumber, updateSku, reviewSku, createSku, exportQuote } from '../../api/price-list/price-list-api';
import { useAuth } from '../../api/auth/AuthContext';
import { useSkuEntitlements, can } from './PriceListEntitlements';
import {
  type SkuDto,
  type CalculatedPrice,
  useCountryCodes,
  useSources,
  useCategories,
  useBrands,
  fmtDate,
  fmtCurrency,
  fmtPercent,
  scoreClass,
  pctToInput,
  fmtCostField,
  costToInput,
  COST_TYPE_LABELS,
} from './PriceListShared';

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const isExpired = (date: string | null | undefined): boolean =>
  date != null && new Date(date) < new Date();

// startDate and notes are returned by the backend inside CalculatedPrice but are absent from the
// auto-generated schema — flag for regeneration when the OpenAPI spec is updated.
type CalculatedPriceExt = CalculatedPrice & { startDate?: string | null; notes?: string | null };

type NumericPriceKey = Exclude<keyof CalculatedPrice, 'isDefault' | 'expiryDate' | 'condition'>;
const breakdownRows: { label: string; key: NumericPriceKey; pct?: boolean; dual?: boolean }[] = [
  { label: 'Impuesto foraneo',  key: 'foreignTax' },
  { label: 'Impuesto nacional', key: 'nationalTax' },
  { label: 'Costos aduana',     key: 'customsCosts' },
  { label: 'Costos Envío',      key: 'shipmentCost' },
  { label: 'Costo Total',       key: 'cost' },
  { label: 'Descuento',         key: 'discount',        pct: true, dual: true },
  { label: 'Utilidad',          key: 'profit' },
  { label: 'Offset lista',      key: 'priceListOffset', pct: true, dual: true },
];

type CardSection = 'general' | 'breakdown' | 'shipment' | 'import' | 'discount' | 'graficas';

// ─── Charts ───────────────────────────────────────────────────────

function polarXY(cx: number, cy: number, r: number, deg: number) {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function donutArcPath(
  cx: number, cy: number,
  outerR: number, innerR: number,
  startDeg: number, endDeg: number,
): string {
  const os = polarXY(cx, cy, outerR, startDeg);
  const oe = polarXY(cx, cy, outerR, endDeg);
  const is_ = polarXY(cx, cy, innerR, startDeg);
  const ie  = polarXY(cx, cy, innerR, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${os.x} ${os.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${oe.x} ${oe.y}`,
    `L ${ie.x} ${ie.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${is_.x} ${is_.y}`,
    `Z`,
  ].join(' ');
}

const PriceCharts: React.FC<{ fp: CalculatedPrice; fixedPrice?: number | string | null }> = ({ fp, fixedPrice }) => {
  const listPrice     = Number(fp.listPrice) || 0;
  const discount      = Number(fp.discount)  || 0;
  const costo         = listPrice * (1 - discount);
  const sellPrice     = fixedPrice != null ? Number(fixedPrice) : (Number(fp.suggestedCustomerPrice) || 0);

  const gastos   = (Number(fp.foreignTax)   || 0) + (Number(fp.nationalTax) || 0)
                 + (Number(fp.customsCosts) || 0) + (Number(fp.shipmentCost) || 0);
  const utilidad = Math.max(0, sellPrice - costo - gastos);

  const maxPrice     = Math.max(listPrice, sellPrice);
  const listRatio    = maxPrice > 0 ? listPrice / maxPrice : 0;
  const sellBarRatio = maxPrice > 0 ? sellPrice / maxPrice : 0;

  const rawSegs = [
    { value: costo,    color: '#0f172a', label: 'Costo' },
    { value: gastos,   color: '#e67e22', label: 'Gastos' },
    { value: utilidad, color: '#27ae60', label: 'Utilidad' },
  ].filter(s => s.value > 0);

  const donutTotal = rawSegs.reduce((acc, s) => acc + s.value, 0);
  const outerR = 48;
  const innerR = 32;
  let startDeg = -90;
  const donutSegs = rawSegs.map((seg) => {
    const sweep = rawSegs.length === 1 ? 359.99 : (seg.value / donutTotal) * 360;
    const endDeg = startDeg + sweep;
    const path = donutArcPath(50, 50, outerR, innerR, startDeg, endDeg);
    startDeg = endDeg;
    return { ...seg, path };
  });

  return (
    <div className="pricelist-charts">
      <div className="pricelist-chart-block">
        <p className="pricelist-chart-label">Precio lista vs. precio venta</p>
        {maxPrice > 0 ? (
          <div className="pricelist-two-bars">
            <div className="pricelist-two-bar-row">
              <span className="pricelist-two-bar-name">Lista</span>
              <div className="pricelist-two-bar-track">
                <div className="pricelist-two-bar-fill pricelist-two-bar-fill--list"
                  style={{ width: `${listRatio * 100}%` }} />
              </div>
              <span className="pricelist-two-bar-val">{fmtCurrency(listPrice)}</span>
            </div>
            <div className="pricelist-two-bar-row">
              <span className="pricelist-two-bar-name">Venta</span>
              <div className="pricelist-two-bar-track">
                <div className="pricelist-two-bar-fill pricelist-two-bar-fill--sell"
                  style={{ width: `${sellBarRatio * 100}%` }} />
              </div>
              <span className="pricelist-two-bar-val">{fmtCurrency(sellPrice)}</span>
            </div>
          </div>
        ) : (
          <p className="pricelist-chart-empty">Sin datos de precio</p>
        )}
      </div>

      {donutTotal > 0 && (
        <div className="pricelist-chart-block">
          <p className="pricelist-chart-label">Composición precio venta</p>
          <div className="pricelist-donut-row">
            <svg className="pricelist-donut-svg" viewBox="0 0 100 100" width="100" height="100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" strokeWidth="16" />
              {donutSegs.map((seg, i) => (
                <path key={i} d={seg.path} fill={seg.color} />
              ))}
              <text x="50" y="47" textAnchor="middle" fontSize="7" fill="#94a3b8" fontWeight="600">VENTA</text>
              <text x="50" y="58" textAnchor="middle" fontSize="8" fill="#0f172a" fontWeight="700">
                {fmtCurrency(sellPrice)}
              </text>
            </svg>
            <div className="pricelist-donut-legend">
              {rawSegs.map((seg, i) => (
                <div key={i} className="pricelist-donut-legend-row">
                  <span className="pricelist-chart-dot" style={{ background: seg.color }} />
                  <span className="pricelist-donut-legend-label">{seg.label}</span>
                  <span className="pricelist-donut-legend-val">{fmtCurrency(seg.value)}</span>
                  <span className="pricelist-donut-legend-pct">
                    {((seg.value / donutTotal) * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SkuCard ──────────────────────────────────────────────────────

const SkuCard: React.FC<{ sku: SkuDto; simDistribPct?: number; simLocked?: boolean; onToggleLock?: () => void }> = ({ sku, simDistribPct, simLocked, onToggleLock }) => {
  const { account } = useAuth();
  const entitlements = useSkuEntitlements();
  const countries = useCountryCodes();
  const sources = useSources();
  const initPriceIdx = (() => { const i = (sku.prices ?? []).findIndex(p => p.isDefault); return i >= 0 ? i : 0; })();

  // Which card sections this user may view — evaluated once at mount via lazy useState init.
  const sectionPermMap: Record<CardSection, boolean> = {
    general:   can.viewSkuDetails(entitlements),
    graficas:  can.viewSkuSummary(entitlements),
    breakdown: can.viewSkuBreakdown(entitlements),
    shipment:  can.viewSkuShipment(entitlements),
    import:    can.viewSkuImportCosts(entitlements),
    discount:  can.viewSkuDiscount(entitlements),
  };

  const [activeSection, setActiveSection] = useState<CardSection | null>(() => {
    if (sectionPermMap.graficas  && (sku.prices?.length ?? 0) > 0) return 'graficas';
    if (sectionPermMap.general)   return 'general';
    if (sectionPermMap.breakdown) return 'breakdown';
    if (sectionPermMap.shipment)  return 'shipment';
    if (sectionPermMap.import)    return 'import';
    if (sectionPermMap.discount)  return 'discount';
    return null;
  });
  const [editingSection, setEditingSection] = useState<CardSection | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [skuData, setSkuData] = useState<SkuDto>(sku);
  const [selectedPriceIdx, setSelectedPriceIdx] = useState<number>(initPriceIdx);
  const [reviewed, setReviewed] = useState(false);
  const [validating, setValidating] = useState(false);
  const [fixedPriceDirty, setFixedPriceDirty] = useState(false);

  const activePrices = skuData.prices ?? [];
  const fp = activePrices[selectedPriceIdx] ?? null;

  const isStale = !skuData.updatedAt ||
    (Date.now() - new Date(skuData.updatedAt as string).getTime()) > ONE_YEAR_MS;
  const isAutoGenerated = !skuData.updatedBy;
  const needsReview = !skuData.reviewedBy && !reviewed;

  const toggle = (s: CardSection) => {
    setActiveSection(prev => prev === s ? null : s);
    setEditingSection(null);
    setSaveError(null);
  };

  const setCheckbox = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft(d => ({ ...d, [key]: String(e.target.checked) }));

  const startEdit = (section: CardSection) => {
    const d: Record<string, string> = {};
    if (section === 'general') {
      d.partNumber  = skuData.partNumber  ?? '';
      d.description = skuData.description ?? '';
      d.discontinued = String(skuData.discontinued ?? false);
      d.notes       = skuData.notes       ?? '';
    } else if (section === 'graficas') {
      d.useFixedPrice   = String(skuData.fixedPrice != null);
      d.fixedPrice      = skuData.fixedPrice != null ? String(Number(skuData.fixedPrice)) : '';
      d.distribDiscount    = '20';
      d.useDistribDiscount = 'false';
    } else if (section === 'shipment') {
      d.foreignShipmentCost = skuData.foreignShipmentCost != null ? String(Number(skuData.foreignShipmentCost)) : '';
      d.domesticShipmentCost = skuData.domesticShipmentCost != null ? String(Number(skuData.domesticShipmentCost)) : '';
      d.weight = skuData.weight != null ? String(Number(skuData.weight)) : '';
      d.volume = skuData.volume != null ? String(Number(skuData.volume)) : '';
    } else if (section === 'import') {
      d.foreignTax      = costToInput(skuData.foreignTax,    skuData.foreignTaxType);
      d.foreignTaxType  = skuData.foreignTaxType  != null ? String(Number(skuData.foreignTaxType))  : '0';
      d.nationalTax     = costToInput(skuData.nationalTax,   skuData.nationalTaxType);
      d.nationalTaxType = skuData.nationalTaxType != null ? String(Number(skuData.nationalTaxType)) : '0';
      d.customsCosts1      = costToInput(skuData.customsCosts1, skuData.customsCosts1Type);
      d.customsCosts1Type  = skuData.customsCosts1Type != null ? String(Number(skuData.customsCosts1Type)) : '0';
      d.customsCosts2      = costToInput(skuData.customsCosts2, skuData.customsCosts2Type);
      d.customsCosts2Type  = skuData.customsCosts2Type != null ? String(Number(skuData.customsCosts2Type)) : '0';
      d.customsCosts3      = costToInput(skuData.customsCosts3, skuData.customsCosts3Type);
      d.customsCosts3Type  = skuData.customsCosts3Type != null ? String(Number(skuData.customsCosts3Type)) : '0';
      d.countryOrigin   = skuData.countryOrigin   ?? '';
      d.countryShipment = skuData.countryShipment ?? '';
    }
    setDraft(d);
    setEditingSection(section);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setDraft({});
    setSaveError(null);
  };

  const handleValidate = async () => {
    if (skuData.skuId == null) return;
    setValidating(true);
    setSaveError(null);
    const reviewer = account?.name ?? account?.username ?? 'Admin';
    await reviewSku(Number(skuData.skuId), reviewer, {
      onSuccess: () => { setReviewed(true); setSkuData(d => ({ ...d, reviewedBy: reviewer })); },
      onBadRequest: (msg) => setSaveError(msg),
      onError: (msg) => setSaveError(msg),
    });
    setValidating(false);
  };

  const saveEdit = async () => {
    if (skuData.skuId == null) return;
    setSaving(true);
    setSaveError(null);

    const reviewer = account?.name ?? account?.username ?? null;
    const prevFixedPrice = skuData.fixedPrice;
    const updated: SkuDto = { ...skuData, updatedBy: reviewer, reviewedBy: reviewer };
    if (editingSection === 'general') {
      updated.partNumber   = draft.partNumber  !== '' ? draft.partNumber  : undefined;
      updated.description  = draft.description !== '' ? draft.description : undefined;
      updated.discontinued = draft.discontinued === 'true';
      updated.notes        = draft.notes !== '' ? draft.notes : undefined;
    } else if (editingSection === 'graficas') {
      updated.fixedPrice   = draft.useFixedPrice === 'true' && draft.fixedPrice !== ''
        ? Number(draft.fixedPrice)
        : null;
    } else if (editingSection === 'shipment') {
      updated.foreignShipmentCost = draft.foreignShipmentCost !== '' ? Number(draft.foreignShipmentCost) : null;
      updated.domesticShipmentCost = draft.domesticShipmentCost !== '' ? Number(draft.domesticShipmentCost) : null;
      updated.weight = draft.weight !== '' ? Number(draft.weight) : null;
      updated.volume = draft.volume !== '' ? Number(draft.volume) : null;
    } else if (editingSection === 'import') {
      const toVal = (v: string, t: string) => v === '' ? null : Number(t) === 2 ? Number(v) : Number(v) / 100;
      updated.foreignTax      = toVal(draft.foreignTax,     draft.foreignTaxType);
      updated.foreignTaxType  = draft.foreignTaxType  !== '' ? Number(draft.foreignTaxType)  : null;
      updated.nationalTax     = toVal(draft.nationalTax,    draft.nationalTaxType);
      updated.nationalTaxType = draft.nationalTaxType !== '' ? Number(draft.nationalTaxType) : null;
      updated.customsCosts1      = toVal(draft.customsCosts1,    draft.customsCosts1Type);
      updated.customsCosts1Type  = draft.customsCosts1Type  !== '' ? Number(draft.customsCosts1Type)  : null;
      updated.customsCosts2      = toVal(draft.customsCosts2,    draft.customsCosts2Type);
      updated.customsCosts2Type  = draft.customsCosts2Type  !== '' ? Number(draft.customsCosts2Type)  : null;
      updated.customsCosts3      = toVal(draft.customsCosts3,    draft.customsCosts3Type);
      updated.customsCosts3Type  = draft.customsCosts3Type  !== '' ? Number(draft.customsCosts3Type)  : null;
      updated.countryOrigin   = draft.countryOrigin   !== '' ? draft.countryOrigin   : null;
      updated.countryShipment = draft.countryShipment !== '' ? draft.countryShipment : null;
    }

    await updateSku(Number(skuData.skuId), updated, {
      onSuccess: () => {
        if (editingSection === 'graficas' && updated.fixedPrice !== prevFixedPrice) {
          setFixedPriceDirty(true);
        }
        setSkuData(updated);
        setEditingSection(null);
        setDraft({});
      },
      onBadRequest: (msg) => setSaveError(msg),
      onError: (msg) => setSaveError(msg),
    });
    setSaving(false);
  };

  const setField = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDraft(d => ({ ...d, [key]: e.target.value }));

  return (
    <div className={`pricelist-card${skuData.discontinued ? ' pricelist-card--discontinued' : ''}${needsReview ? ' pricelist-card--needs-review' : ''}`}>
      <div className="pricelist-card-header">
        <div>
          <div className="pricelist-card-title">
            {skuData.partNumber ?? '—'}
            {skuData.discontinued && (
              <span className="pricelist-badge pricelist-badge--discontinued">Descontinuado</span>
            )}
          </div>
          <div className="pricelist-card-subtitle">{skuData.description ?? '—'}</div>
          <div className="pricelist-card-category">{skuData.categoryType ?? '—'}</div>
          <div className="pricelist-card-category">{skuData.categoryName ?? '—'}</div>
          { sku.tag1 && (<div className="pricelist-card-category">{skuData.tag1 ?? '—'}</div>) }
          { sku.tag2 && (<div className="pricelist-card-category">{skuData.tag2 ?? '—'}</div>) }
          { sku.tag3 && (<div className="pricelist-card-category">{skuData.tag3 ?? '—'}</div>) }
        </div>
        <div className="pricelist-card-header-badges">
          {needsReview && (
            <span
              className="pricelist-badge pricelist-badge--review"
              title={isAutoGenerated ? 'Generado automáticamente — sin revisar' : isStale ? 'Sin actualizar por más de un año — sin revisar' : 'Sin revisar'}
            >
              Sin Validar
            </span>
          )}
          {skuData.discountScore != null && (
            Number(skuData.discountScore) >= 1
              ? <span className="pricelist-badge pricelist-badge--green">✓</span>
              : <span className={`pricelist-badge ${scoreClass(skuData.discountScore)}`}>
                  {(Number(skuData.discountScore) * 100).toFixed(0)}%
                </span>
          )}
          {onToggleLock && (
            <button
              className={`pricelist-lock-btn${simLocked ? ' pricelist-lock-btn--locked' : ''}`}
              onClick={onToggleLock}
              title={simLocked ? `Bloqueado en ${simDistribPct}%` : 'Fijar descuento actual para este SKU'}
            >
              {simLocked ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="pricelist-card-meta">
        <span><strong>Marca:</strong> {skuData.brandName}</span>
        {can.viewSkuShipment(entitlements) && (
          <span><strong>Peso:</strong> {skuData.weight != null ? `${skuData.weight} lb` : '—'}</span>
        )}
      </div>
      {activePrices.length > 0 && can.viewSkuDiscount(entitlements) && (
        <div className="pricelist-price-selector">
          <span className="pricelist-price-selector-label">Descuento:</span>
          {activePrices.map((p, i) => {
            const expired = isExpired(p.expiryDate);
            return (
              <button
                key={i}
                className={`pricelist-price-pill${selectedPriceIdx === i ? ' pricelist-price-pill--active' : ''}${expired ? ' pricelist-price-pill--expired' : ''}`}
                onClick={() => setSelectedPriceIdx(i)}
              >
                {p.discount != null ? `${Math.round(Number(p.discount) * 100)}%` : '—'}
                {expired && <span className="pricelist-price-pill-expired" aria-label="Descuento vencido"> !</span>}
                {p.isDefault && <span className="pricelist-price-pill-star"> ★</span>}
              </button>
            );
          })}
        </div>
      )}
      {skuData.notes && (
        <div className="pricelist-card-notes">{skuData.notes}</div>
      )}

      {fp && (
        <div className="pricelist-card-summary">
          {can.viewSkuBreakdown(entitlements) && (
            <div className="pricelist-summary-item">
              <span className="pricelist-summary-label">
                Precio lista
                {(skuData.priceMismatch || fp.listPrice == null || Number(fp.listPrice) === 0) && (
                  <span className="pricelist-price-warn" data-tooltip="Revisar lista de precio" aria-label="Revisar lista de precio">⚠</span>
                )}
              </span>
              <span className="pricelist-summary-value">{fmtCurrency(fp.listPrice)}</span>
            </div>
          )}
          <div className="pricelist-summary-item pricelist-summary-item--highlight">
            <span className="pricelist-summary-label">Precio sugerido</span>
            <span className="pricelist-summary-value">{fmtCurrency(fp.suggestedCustomerPrice)}</span>
          </div>
          {skuData.fixedPrice != null && (
            <div className="pricelist-summary-item pricelist-summary-item--fixed">
              <span className="pricelist-summary-label">Precio venta final</span>
              <span className="pricelist-summary-value">{fmtCurrency(skuData.fixedPrice)}</span>
            </div>
          )}
          {simDistribPct != null && (() => {
            const baseSell = skuData.fixedPrice != null ? Number(skuData.fixedPrice) : Number(fp.suggestedCustomerPrice) || 0;
            const cost = Number(fp.cost) || 0;
            const baseProfit = baseSell - cost;
            const distribSell = baseSell * (1 - simDistribPct / 100);
            const profitAfterDiscount = distribSell - cost;
            const profitDrop = baseProfit - profitAfterDiscount;
            const profitDropPct = baseProfit !== 0 ? profitDrop / Math.abs(baseProfit) : 0;
            return (
              <>
                <div className="pricelist-sim-sep">Simulación · {simDistribPct}% dto. dist.{simLocked ? ' · fijo' : ''}</div>
                <div className="pricelist-summary-item">
                  <span className="pricelist-summary-label">Precio distribuidor</span>
                  <span className="pricelist-summary-value pricelist-sim-price">{fmtCurrency(distribSell)}</span>
                </div>
                <div className="pricelist-summary-item">
                  <span className="pricelist-summary-label">Utilidad final</span>
                  <span className={`pricelist-summary-value ${profitAfterDiscount < 0 ? 'pricelist-sim-loss' : 'pricelist-sim-profit'}`}>
                    {fmtCurrency(profitAfterDiscount)}&nbsp;<span className="pricelist-sim-drop">↓ {fmtCurrency(profitDrop)} · {fmtPercent(profitDropPct)}</span>
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      )}

      <div className="pricelist-section-pills">
        {((['general' as CardSection] as CardSection[])
          .concat(fp ? ['graficas' as CardSection] : [])
          .concat(['breakdown', 'shipment', 'import', 'discount'])
          .filter(s => sectionPermMap[s])
        ).map((s) => {
          const labels: Record<CardSection, string> = {
            general: 'General', breakdown: 'Desglose', shipment: 'Envío',
            import: 'Importación', discount: 'Descuento', graficas: 'Resumen',
          };
          return (
            <button
              key={s}
              className={`pricelist-section-pill${activeSection === s ? ' pricelist-section-pill--active' : ''}`}
              onClick={() => toggle(s)}
            >
              {labels[s]}
            </button>
          );
        })}
      </div>

      {activeSection === 'general' && (
        <div className="pricelist-section-panel">
          {editingSection !== 'general' ? (
            <>
              <div className="pricelist-panel-header">
                {needsReview && (
                  <button className="pricelist-validate-btn" onClick={handleValidate} disabled={validating}>
                    {validating ? 'Guardando…' : 'Validar'}
                  </button>
                )}
                {can.updateSkuDetails(entitlements) && (
                  <button className="pricelist-edit-btn" onClick={() => startEdit('general')}>Editar</button>
                )}
              </div>
              {needsReview && (
                <div className="pricelist-review-notice">
                  <span>⚠</span>
                  <span>{isAutoGenerated ? 'Generado automáticamente — revisar que la información es correcta' : isStale ? 'Sin actualizar por más de un año — revisar que los datos están vigentes' : 'Este registro no ha sido validado — confirme que los datos son correctos'}</span>
                </div>
              )}
              <div className="pricelist-kv-grid">
                <span className="pricelist-kv-label">Número de parte</span>
                <span className="pricelist-kv-value">{skuData.partNumber ?? '—'}</span>
                <span className="pricelist-kv-label">Descripción</span>
                <span className="pricelist-kv-value">{skuData.description ?? '—'}</span>
                <span className="pricelist-kv-label">Fuente</span>
                <span className="pricelist-kv-value pricelist-kv-value--muted">
                  {skuData.source
                    ? [
                        sources.find(s => s.SourceId === skuData.source)?.Name ?? skuData.source,
                        skuData.pageRef != null ? `pag. ${skuData.pageRef}` : null,
                      ].filter(Boolean).join(' ')
                    : '—'}
                </span>
                <span className="pricelist-kv-label">Descontinuado</span>
                <span className="pricelist-kv-value">{skuData.discontinued ? 'Sí' : 'No'}</span>
                <span className="pricelist-kv-label">Notas</span>
                <span className="pricelist-kv-value">{skuData.notes ?? '—'}</span>
                <span className="pricelist-kv-label">Actualizado</span>
                <span className="pricelist-kv-value pricelist-kv-value--muted">
                  {[skuData.updatedBy ?? 'Automático', fmtDate(skuData.updatedAt)].join(' · ')}
                </span>
              </div>
            </>
          ) : (
            <div className="pricelist-edit-form">
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Número de parte</label>
                <input className="pricelist-edit-input" type="text"
                  value={draft.partNumber ?? ''} onChange={setField('partNumber')} />
              </div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Descripción</label>
                <input className="pricelist-edit-input" type="text"
                  value={draft.description ?? ''} onChange={setField('description')} />
              </div>
              <div className="pricelist-edit-field pricelist-edit-field--inline">
                <label className="pricelist-edit-label">Descontinuado</label>
                <input type="checkbox"
                  checked={draft.discontinued === 'true'}
                  onChange={setCheckbox('discontinued')} />
              </div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Notas</label>
                <textarea className="pricelist-edit-textarea"
                  rows={3}
                  value={draft.notes ?? ''}
                  onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                />
              </div>
              {saveError && <p className="pricelist-save-error">{saveError}</p>}
              <div className="pricelist-edit-actions">
                <button className="pricelist-cancel-btn" onClick={cancelEdit} disabled={saving}>Cancelar</button>
                <button className="pricelist-save-btn" onClick={saveEdit} disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'breakdown' && fp && (
        <div className="pricelist-section-panel">
          {fixedPriceDirty && (
            <div className="pricelist-breakdown-notice">
              <span>⚠</span>
              <span>El precio fijo fue actualizado. Vuelva a buscar el número de parte para ver el desglose actualizado.</span>
            </div>
          )}
          <table className="pricelist-price-table">
            <tbody>
              {breakdownRows.flatMap(({ label, key, pct, dual }) => {
                const val = fp![key];
                const cell = dual
                  ? `${fmtCurrency(Number(fp!.listPrice) * Number(val))} (${fmtPercent(val)})`
                  : pct ? fmtPercent(val) : fmtCurrency(val);
                const row = <tr key={key}><td>{label}</td><td>{cell}</td></tr>;
                if (key === 'discount') {
                  const lp = Number(fp!.listPrice) || 0;
                  const disc = Number(fp!.discount) || 0;
                  return [row, (
                    <tr key="__discounted__">
                      <td>Precio con descuento</td>
                      <td>{fmtCurrency(lp * (1 - disc))}</td>
                    </tr>
                  )];
                }
                return [row];
              })}
            </tbody>
          </table>
          {simDistribPct != null && (() => {
            const baseSell = skuData.fixedPrice != null ? Number(skuData.fixedPrice) : Number(fp.suggestedCustomerPrice) || 0;
            const cost = Number(fp.cost) || 0;
            const baseProfit = baseSell - cost;
            const distribSell = baseSell * (1 - simDistribPct / 100);
            const profitAfterDiscount = distribSell - cost;
            const profitDrop = baseProfit - profitAfterDiscount;
            const profitDropPct = baseProfit !== 0 ? profitDrop / Math.abs(baseProfit) : 0;
            return (
              <div className="pricelist-sim-block">
                <div className="pricelist-sim-block-header">Simulación · {simDistribPct}% dto. distribuidor</div>
                <table className="pricelist-price-table">
                  <tbody>
                    <tr><td>Precio venta base</td><td>{fmtCurrency(baseSell)}</td></tr>
                    <tr><td>Utilidad base</td><td>{fmtCurrency(baseProfit)}</td></tr>
                    <tr><td>Precio distribuidor</td><td className="pricelist-sim-price">{fmtCurrency(distribSell)}</td></tr>
                    <tr>
                      <td>Utilidad final</td>
                      <td className={profitAfterDiscount < 0 ? 'pricelist-sim-loss' : 'pricelist-sim-profit'}>
                        {fmtCurrency(profitAfterDiscount)}
                      </td>
                    </tr>
                    <tr>
                      <td>Caída de utilidad</td>
                      <td className="pricelist-sim-drop">↓ {fmtCurrency(profitDrop)} · {fmtPercent(profitDropPct)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {activeSection === 'shipment' && (
        <div className="pricelist-section-panel">
          {editingSection !== 'shipment' ? (
            <>
              <div className="pricelist-panel-header">
                {can.updateSkuShipment(entitlements) && (
                  <button className="pricelist-edit-btn" onClick={() => startEdit('shipment')}>Editar</button>
                )}
              </div>
              <div className="pricelist-kv-grid">
                <span className="pricelist-kv-label">Envío exterior</span>
                <span className="pricelist-kv-value">{fmtCurrency(skuData.foreignShipmentCost)}</span>
                <span className="pricelist-kv-label">Envío nacional</span>
                <span className="pricelist-kv-value">{fmtCurrency(skuData.domesticShipmentCost)}</span>
                <span className="pricelist-kv-label">Peso</span>
                <span className="pricelist-kv-value">{skuData.weight != null ? `${skuData.weight} lb` : '—'}</span>
                <span className="pricelist-kv-label">Volumen</span>
                <span className="pricelist-kv-value">{skuData.volume != null ? `${skuData.volume} m³` : '—'}</span>
              </div>
            </>
          ) : (
            <div className="pricelist-edit-form">
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Envío exterior (USD)</label>
                <input className="pricelist-edit-input" type="number" step="0.01" min="0"
                  value={draft.foreignShipmentCost} onChange={setField('foreignShipmentCost')} />
              </div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Envío nacional (USD)</label>
                <input className="pricelist-edit-input" type="number" step="0.01" min="0"
                  value={draft.domesticShipmentCost} onChange={setField('domesticShipmentCost')} />
              </div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Peso (lb)</label>
                <input className="pricelist-edit-input" type="number" step="0.001" min="0"
                  value={draft.weight} onChange={setField('weight')} />
              </div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Volumen (m³)</label>
                <input className="pricelist-edit-input" type="number" step="0.0001" min="0"
                  value={draft.volume} onChange={setField('volume')} />
              </div>
              {saveError && <p className="pricelist-save-error">{saveError}</p>}
              <div className="pricelist-edit-actions">
                <button className="pricelist-cancel-btn" onClick={cancelEdit} disabled={saving}>Cancelar</button>
                <button className="pricelist-save-btn" onClick={saveEdit} disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'import' && (
        <div className="pricelist-section-panel">
          {editingSection !== 'import' ? (
            <>
              <div className="pricelist-panel-header">
                {can.updateSkuImportCosts(entitlements) && (
                  <button className="pricelist-edit-btn" onClick={() => startEdit('import')}>Editar</button>
                )}
              </div>
              <div className="pricelist-journey">

                <div className="pricelist-journey-step">
                  <div className="pricelist-journey-spine">
                    <div className="pricelist-journey-dot" />
                    <div className="pricelist-journey-line" />
                  </div>
                  <div className="pricelist-journey-body">
                    <div className="pricelist-journey-step-label">Origen</div>
                    {skuData.countryOrigin
                      ? <div className="pricelist-journey-country">{skuData.countryOrigin}</div>
                      : <div className="pricelist-journey-empty">—</div>
                    }
                  </div>
                </div>

                <div className="pricelist-journey-step">
                  <div className="pricelist-journey-spine">
                    <div className="pricelist-journey-dot" />
                    <div className="pricelist-journey-line" />
                  </div>
                  <div className="pricelist-journey-body">
                    <div className="pricelist-journey-step-label">País de envío · Impuesto extranjero</div>
                    <div className="pricelist-journey-cost-row">
                      <span className="pricelist-journey-cost-label">País envío</span>
                      <span className="pricelist-journey-cost-val">{skuData.countryShipment ?? '—'}</span>
                    </div>
                    <div className="pricelist-journey-cost-row">
                      <span className="pricelist-journey-cost-label">Impuesto extranjero</span>
                      <div className="pricelist-journey-cost-val">
                        {fmtCostField(skuData.foreignTax, skuData.foreignTaxType)}
                        {skuData.foreignTaxType != null && skuData.foreignTax != null && (
                          <span className="pricelist-journey-cost-type">{COST_TYPE_LABELS[String(Number(skuData.foreignTaxType))]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pricelist-journey-step">
                  <div className="pricelist-journey-spine">
                    <div className="pricelist-journey-dot" />
                    <div className="pricelist-journey-line" />
                  </div>
                  <div className="pricelist-journey-body">
                    <div className="pricelist-journey-step-label">Aduana</div>
                    {([
                      { label: 'Costo 1', val: skuData.customsCosts1, type: skuData.customsCosts1Type },
                      { label: 'Costo 2', val: skuData.customsCosts2, type: skuData.customsCosts2Type },
                      { label: 'Costo 3', val: skuData.customsCosts3, type: skuData.customsCosts3Type },
                    ] as Array<{ label: string; val: typeof skuData.customsCosts1; type: typeof skuData.customsCosts1Type }>).map(({ label, val, type }) => (
                      <div key={label} className="pricelist-journey-cost-row">
                        <span className="pricelist-journey-cost-label">{label}</span>
                        <div className="pricelist-journey-cost-val">
                          {fmtCostField(val, type)}
                          {type != null && val != null && (
                            <span className="pricelist-journey-cost-type">{COST_TYPE_LABELS[String(Number(type))]}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pricelist-journey-step">
                  <div className="pricelist-journey-spine">
                    <div className="pricelist-journey-dot pricelist-journey-dot--dest" />
                  </div>
                  <div className="pricelist-journey-body">
                    <div className="pricelist-journey-step-label">Destino · Impuesto nacional</div>
                    <div className="pricelist-journey-cost-row">
                      <span className="pricelist-journey-cost-label">Impuesto nacional</span>
                      <div className="pricelist-journey-cost-val">
                        {fmtCostField(skuData.nationalTax, skuData.nationalTaxType)}
                        {skuData.nationalTaxType != null && skuData.nationalTax != null && (
                          <span className="pricelist-journey-cost-type">{COST_TYPE_LABELS[String(Number(skuData.nationalTaxType))]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </>
          ) : (
            <div className="pricelist-edit-form">

              <div className="pricelist-journey-edit-group">
                <div className="pricelist-journey-edit-header">Origen</div>
                <div className="pricelist-edit-field">
                  <label className="pricelist-edit-label">País origen</label>
                  <select className="pricelist-edit-select pricelist-edit-select--country"
                    value={draft.countryOrigin ?? ''} onChange={setField('countryOrigin')}>
                    <option value="">— Sin selección —</option>
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pricelist-journey-edit-group">
                <div className="pricelist-journey-edit-header">País de envío · Impuesto extranjero</div>
                <div className="pricelist-edit-field">
                  <label className="pricelist-edit-label">País envío</label>
                  <select className="pricelist-edit-select pricelist-edit-select--country"
                    value={draft.countryShipment ?? ''} onChange={setField('countryShipment')}>
                    <option value="">— Sin selección —</option>
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="pricelist-edit-field">
                  <label className="pricelist-edit-label">Impuesto extranjero</label>
                  <div className="pricelist-cost-row">
                    <select className="pricelist-edit-select" value={draft.foreignTaxType ?? '0'} onChange={setField('foreignTaxType')}>
                      <option value="0">% precio lista</option>
                      <option value="1">% precio c/dto.</option>
                      <option value="2">Monto fijo</option>
                    </select>
                    <input className="pricelist-edit-input" type="number"
                      step={draft.foreignTaxType === '2' ? '0.01' : '0.0001'} min="0"
                      placeholder={draft.foreignTaxType === '2' ? 'USD' : '%'}
                      value={draft.foreignTax ?? ''} onChange={setField('foreignTax')} />
                  </div>
                </div>
              </div>

              <div className="pricelist-journey-edit-group">
                <div className="pricelist-journey-edit-header">Aduana</div>
                {([
                  { label: 'Costo aduana 1', valKey: 'customsCosts1', typeKey: 'customsCosts1Type' },
                  { label: 'Costo aduana 2', valKey: 'customsCosts2', typeKey: 'customsCosts2Type' },
                  { label: 'Costo aduana 3', valKey: 'customsCosts3', typeKey: 'customsCosts3Type' },
                ] as Array<{ label: string; valKey: string; typeKey: string }>).map(({ label, valKey, typeKey }) => (
                  <div key={valKey} className="pricelist-edit-field">
                    <label className="pricelist-edit-label">{label}</label>
                    <div className="pricelist-cost-row">
                      <select className="pricelist-edit-select" value={draft[typeKey] ?? '0'} onChange={setField(typeKey)}>
                        <option value="0">% precio lista</option>
                        <option value="1">% precio c/dto.</option>
                        <option value="2">Monto fijo</option>
                      </select>
                      <input className="pricelist-edit-input" type="number"
                        step={draft[typeKey] === '2' ? '0.01' : '0.0001'} min="0"
                        placeholder={draft[typeKey] === '2' ? 'USD' : '%'}
                        value={draft[valKey] ?? ''} onChange={setField(valKey)} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="pricelist-journey-edit-group">
                <div className="pricelist-journey-edit-header">Destino · Impuesto nacional</div>
                <div className="pricelist-edit-field">
                  <label className="pricelist-edit-label">Impuesto nacional</label>
                  <div className="pricelist-cost-row">
                    <select className="pricelist-edit-select" value={draft.nationalTaxType ?? '0'} onChange={setField('nationalTaxType')}>
                      <option value="0">% precio lista</option>
                      <option value="1">% precio c/dto.</option>
                      <option value="2">Monto fijo</option>
                    </select>
                    <input className="pricelist-edit-input" type="number"
                      step={draft.nationalTaxType === '2' ? '0.01' : '0.0001'} min="0"
                      placeholder={draft.nationalTaxType === '2' ? 'USD' : '%'}
                      value={draft.nationalTax ?? ''} onChange={setField('nationalTax')} />
                  </div>
                </div>
              </div>

              {saveError && <p className="pricelist-save-error">{saveError}</p>}
              <div className="pricelist-edit-actions">
                <button className="pricelist-cancel-btn" onClick={cancelEdit} disabled={saving}>Cancelar</button>
                <button className="pricelist-save-btn" onClick={saveEdit} disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'graficas' && fp && (
        <div className="pricelist-section-panel">
          {(() => {
            const lp = Number(fp.listPrice) || 0;
            const fixedPriceStep = lp >= 1000 ? 100 : lp >= 100 ? 10 : 1;

            const baseSell = editingSection === 'graficas'
              ? (draft.useFixedPrice === 'true' && draft.fixedPrice !== ''
                  ? Number(draft.fixedPrice)
                  : Number(fp.suggestedCustomerPrice) || 0)
              : null;
            const simDistrib = editingSection === 'graficas' && draft.useDistribDiscount === 'true';
            const distribRate = simDistrib ? Number(draft.distribDiscount ?? '20') / 100 : 0;
            const pageSimBaseSell = skuData.fixedPrice != null
              ? Number(skuData.fixedPrice)
              : Number(fp.suggestedCustomerPrice) || 0;
            const chartFixedPrice = editingSection === 'graficas'
              ? baseSell! * (1 - distribRate)
              : (simDistribPct != null ? pageSimBaseSell * (1 - simDistribPct / 100) : skuData.fixedPrice);

            return (
              <>
                {editingSection !== 'graficas' ? (
                  <div className="pricelist-panel-header">
                    {can.updateSkuPrice(entitlements) && (
                      <button className="pricelist-edit-btn" onClick={() => startEdit('graficas')}>Editar Precio Venta</button>
                    )}
                  </div>
                ) : (
                  <div className="pricelist-edit-form pricelist-edit-form--inline">
                    <div className="pricelist-edit-field pricelist-edit-field--inline">
                      <label className="pricelist-edit-label">Precio fijo</label>
                      <input type="checkbox"
                        checked={draft.useFixedPrice === 'true'}
                        onChange={setCheckbox('useFixedPrice')} />
                    </div>
                    {draft.useFixedPrice === 'true' && (
                      <div className="pricelist-edit-field">
                        <label className="pricelist-edit-label">Precio fijo (USD)</label>
                        <input className="pricelist-edit-input" type="number" step={fixedPriceStep} min="0"
                          value={draft.fixedPrice ?? ''} onChange={setField('fixedPrice')} autoFocus />
                      </div>
                    )}
                    {can.simulateCustomerDiscount(entitlements) && (
                      <div className="pricelist-distrib-field">
                        <div className="pricelist-edit-field pricelist-edit-field--inline">
                          <label className="pricelist-edit-label">Simular dto. distribuidor</label>
                          <input type="checkbox"
                            checked={draft.useDistribDiscount === 'true'}
                            onChange={setCheckbox('useDistribDiscount')} />
                        </div>
                        {draft.useDistribDiscount === 'true' && (
                          <>
                            <div className="pricelist-distrib-header">
                              <span className="pricelist-edit-label">Descuento distribuidor</span>
                              <span className="pricelist-distrib-value">{draft.distribDiscount ?? '20'}%</span>
                            </div>
                            <input
                              className="pricelist-distrib-slider"
                              type="range" step="1" min="0" max="50"
                              value={draft.distribDiscount ?? '20'}
                              onChange={setField('distribDiscount')}
                            />
                            <div className="pricelist-distrib-hint">
                              Precio distribuidor: {fmtCurrency(baseSell! * (1 - distribRate))}
                              &nbsp;·&nbsp;Simulación — no se guarda
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {saveError && <p className="pricelist-save-error">{saveError}</p>}
                    <div className="pricelist-edit-actions">
                      <button className="pricelist-cancel-btn" onClick={cancelEdit} disabled={saving}>Cancelar</button>
                      <button className="pricelist-save-btn" onClick={saveEdit} disabled={saving}>
                        {saving ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                )}
                <PriceCharts fp={fp} fixedPrice={chartFixedPrice} />
              </>
            );
          })()}
        </div>
      )}

      {activeSection === 'discount' && (
        <div className="pricelist-section-panel">
          {!fp ? (
            <div className="pricelist-kv-grid">
              <span className="pricelist-kv-label">Descuento</span>
              <span className="pricelist-kv-value">—</span>
            </div>
          ) : (
            <div className="pricelist-kv-grid">
              <span className="pricelist-kv-label">Condición</span>
              <span className="pricelist-kv-value">
                {Math.round(Number(fp.discount ?? "0") * 100) > 0 ? (fp.condition ?? 'Descuento a distribuidor') : 'Sin Descuento'}
              </span>
              <span className="pricelist-kv-label">Descuento</span>
              <span className="pricelist-kv-value">{fmtPercent(fp.discount)}</span>
              <span className="pricelist-kv-label">Precio lista</span>
              <span className="pricelist-kv-value">{fmtCurrency(fp.listPrice)}</span>
              <span className="pricelist-kv-label">Costo total</span>
              <span className="pricelist-kv-value">{fmtCurrency(fp.cost)}</span>
              <span className="pricelist-kv-label">Precio sugerido</span>
              <span className="pricelist-kv-value">{fmtCurrency(fp.suggestedCustomerPrice)}</span>
              <span className="pricelist-kv-label">Utilidad</span>
              <span className="pricelist-kv-value">{fmtCurrency(fp.profit)} ({fmtPercent(fp.profitPercent)})</span>
              {(fp as CalculatedPriceExt).startDate && (
                <>
                  <span className="pricelist-kv-label">Inicio</span>
                  <span className="pricelist-kv-value">{fmtDate((fp as CalculatedPriceExt).startDate)}</span>
                </>
              )}
              {fp.expiryDate && (
                <>
                  <span className={`pricelist-kv-label${isExpired(fp.expiryDate) ? ' pricelist-kv-label--expired' : ''}`}>Vencimiento</span>
                  <span className={`pricelist-kv-value${isExpired(fp.expiryDate) ? ' pricelist-kv-value--expired' : ''}`}>{fmtDate(fp.expiryDate)}</span>
                </>
              )}
              {(fp as CalculatedPriceExt).notes && (
                <>
                  <span className="pricelist-kv-label">Notas</span>
                  <span className="pricelist-kv-value">{(fp as CalculatedPriceExt).notes}</span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── SkuCreateForm ────────────────────────────────────────────────

type CreateTab = 'general' | 'shipment' | 'import';

const EMPTY_CREATE_DRAFT: Record<string, string> = {
  partNumber: '', skuName: '', description: '', skuType: '', brandId: '', listPrice: '',
  categoryId: '', notes: '', discontinued: 'false',
  foreignShipmentCost: '', domesticShipmentCost: '', weight: '', volume: '',
  countryOrigin: '', countryShipment: '',
  foreignTax: '', foreignTaxType: '0',
  nationalTax: '', nationalTaxType: '0',
  customsCosts1: '', customsCosts1Type: '0',
  customsCosts2: '', customsCosts2Type: '0',
  customsCosts3: '', customsCosts3Type: '0',
};

const CREATE_TAB_LABELS: Record<CreateTab, string> = {
  general: 'General', shipment: 'Envío', import: 'Importación',
};

const SkuCreateForm: React.FC<{
  onSaved: (sku: SkuDto) => void;
  onCancel: () => void;
}> = ({ onSaved, onCancel }) => {
  const { account } = useAuth();
  const countries = useCountryCodes();
  const brands = useBrands();
  const categories = useCategories();

  const [tab, setTab] = useState<CreateTab>('general');
  const [draft, setDraft] = useState<Record<string, string>>(EMPTY_CREATE_DRAFT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneQuery, setCloneQuery] = useState('');
  const [cloneResults, setCloneResults] = useState<SkuDto[] | null>(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  const setField = (key: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setDraft(d => ({ ...d, [key]: e.target.value }));

  const setCheckboxCreate = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft(d => ({ ...d, [key]: String(e.target.checked) }));

  const handleCloneSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cloneQuery.trim()) return;
    setCloneLoading(true);
    setCloneError(null);
    setCloneResults(null);
    await searchSkuByPartNumber(cloneQuery.trim(), {
      onSuccess: (data) => setCloneResults(data),
      onNotFound: () => setCloneError('No se encontraron resultados'),
      onBadRequest: (msg) => setCloneError(msg),
      onError: (msg) => setCloneError(msg),
    });
    setCloneLoading(false);
  };

  const applyClone = (src: SkuDto) => {
    const matchedBrand = brands.find(
      b => b.Id === src.brandName || b.Name === src.brandName
    );
    setDraft({
      partNumber: '',
      skuName: src.skuName ?? '',
      description: src.description ?? '',
      skuType: src.skuType ?? '',
      brandId: matchedBrand?.Id ?? '',
      listPrice: src.listPrice != null ? String(Number(src.listPrice)) : '',
      categoryId: src.categoryId != null ? String(Number(src.categoryId)) : '',
      notes: src.notes ?? '',
      discontinued: String(src.discontinued ?? false),
      foreignShipmentCost: src.foreignShipmentCost != null ? String(Number(src.foreignShipmentCost)) : '',
      domesticShipmentCost: src.domesticShipmentCost != null ? String(Number(src.domesticShipmentCost)) : '',
      weight: src.weight != null ? String(Number(src.weight)) : '',
      volume: src.volume != null ? String(Number(src.volume)) : '',
      countryOrigin: src.countryOrigin ?? '',
      countryShipment: src.countryShipment ?? '',
      foreignTax: costToInput(src.foreignTax, src.foreignTaxType),
      foreignTaxType: src.foreignTaxType != null ? String(Number(src.foreignTaxType)) : '0',
      nationalTax: costToInput(src.nationalTax, src.nationalTaxType),
      nationalTaxType: src.nationalTaxType != null ? String(Number(src.nationalTaxType)) : '0',
      customsCosts1: costToInput(src.customsCosts1, src.customsCosts1Type),
      customsCosts1Type: src.customsCosts1Type != null ? String(Number(src.customsCosts1Type)) : '0',
      customsCosts2: costToInput(src.customsCosts2, src.customsCosts2Type),
      customsCosts2Type: src.customsCosts2Type != null ? String(Number(src.customsCosts2Type)) : '0',
      customsCosts3: costToInput(src.customsCosts3, src.customsCosts3Type),
      customsCosts3Type: src.customsCosts3Type != null ? String(Number(src.customsCosts3Type)) : '0',
    });
    setCloneOpen(false);
    setCloneQuery('');
    setCloneResults(null);
    setTab('general');
  };

  const handleSave = async () => {
    const missing: string[] = [];
    if (!draft.partNumber.trim()) missing.push('Número de parte');
    if (!draft.brandId) missing.push('Marca');
    if (!draft.listPrice || Number(draft.listPrice) <= 0) missing.push('Precio lista');
    if (missing.length > 0) {
      setSaveError(`Campos obligatorios: ${missing.join(', ')}`);
      setTab('general');
      return;
    }
    setSaving(true);
    setSaveError(null);

    const reviewer = account?.name ?? account?.username ?? null;
    const toVal = (v: string, t: string) =>
      v === '' ? null : Number(t) === 2 ? Number(v) : Number(v) / 100;

    const dto: SkuDto = {
      partNumber: draft.partNumber.trim(),
      skuName: draft.skuName || undefined,
      description: draft.description || undefined,
      skuType: draft.skuType || undefined,
      categoryId: draft.categoryId !== '' ? Number(draft.categoryId) : null,
      brandName: draft.brandId,
      listPrice: Number(draft.listPrice),
      notes: draft.notes || undefined,
      discontinued: draft.discontinued === 'true',
      foreignShipmentCost: draft.foreignShipmentCost !== '' ? Number(draft.foreignShipmentCost) : null,
      domesticShipmentCost: draft.domesticShipmentCost !== '' ? Number(draft.domesticShipmentCost) : null,
      weight: draft.weight !== '' ? Number(draft.weight) : null,
      volume: draft.volume !== '' ? Number(draft.volume) : null,
      countryOrigin: draft.countryOrigin || null,
      countryShipment: draft.countryShipment || null,
      foreignTax: toVal(draft.foreignTax ?? '', draft.foreignTaxType ?? '0'),
      foreignTaxType: draft.foreignTaxType !== '' ? Number(draft.foreignTaxType) : null,
      nationalTax: toVal(draft.nationalTax ?? '', draft.nationalTaxType ?? '0'),
      nationalTaxType: draft.nationalTaxType !== '' ? Number(draft.nationalTaxType) : null,
      customsCosts1: toVal(draft.customsCosts1 ?? '', draft.customsCosts1Type ?? '0'),
      customsCosts1Type: draft.customsCosts1Type !== '' ? Number(draft.customsCosts1Type) : null,
      customsCosts2: toVal(draft.customsCosts2 ?? '', draft.customsCosts2Type ?? '0'),
      customsCosts2Type: draft.customsCosts2Type !== '' ? Number(draft.customsCosts2Type) : null,
      customsCosts3: toVal(draft.customsCosts3 ?? '', draft.customsCosts3Type ?? '0'),
      customsCosts3Type: draft.customsCosts3Type !== '' ? Number(draft.customsCosts3Type) : null,
      updatedBy: reviewer,
      reviewedBy: reviewer,
    };

    let createdSku: SkuDto | null = null;
    await createSku(dto, {
      onSuccess: (created) => { createdSku = created; },
      onBadRequest: (msg) => setSaveError(msg),
      onError: (msg) => setSaveError(msg),
    });

    if (createdSku) onSaved(createdSku);
    setSaving(false);
  };

  return (
    <div className="skucreate-panel">
      <div className="skucreate-header">
        <span className="skucreate-title">Nuevo SKU</span>
        <button className="skucreate-close" onClick={onCancel} aria-label="Cerrar">✕</button>
      </div>

      <div className="skucreate-clone">
        <button
          className="skucreate-clone-toggle"
          onClick={() => { setCloneOpen(o => !o); setCloneResults(null); setCloneError(null); }}
        >
          <span>{cloneOpen ? '▾' : '▸'}</span>
          Clonar desde número de parte existente
        </button>
        {cloneOpen && (
          <div className="skucreate-clone-body">
            <form className="skucreate-clone-row" onSubmit={handleCloneSearch}>
              <input
                className="pricelist-edit-input"
                type="text"
                placeholder="Número de parte…"
                value={cloneQuery}
                onChange={e => setCloneQuery(e.target.value)}
                disabled={cloneLoading}
                autoFocus
              />
              <button className="pricelist-save-btn" type="submit" disabled={cloneLoading || !cloneQuery.trim()}>
                {cloneLoading ? 'Buscando…' : 'Buscar'}
              </button>
            </form>
            {cloneError && <p className="pricelist-save-error">{cloneError}</p>}
            {cloneResults && cloneResults.length > 0 && (
              <div className="skucreate-clone-results">
                {cloneResults.map((s, i) => (
                  <div key={i} className="skucreate-clone-result">
                    <div className="skucreate-clone-result-info">
                      <span className="skucreate-clone-result-pn">{s.partNumber}</span>
                      {s.description && (
                        <span className="skucreate-clone-result-desc">{s.description}</span>
                      )}
                    </div>
                    <button className="pricelist-save-btn skucreate-clone-btn" type="button" onClick={() => applyClone(s)}>
                      Clonar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="skucreate-tabs">
        {(['general', 'shipment', 'import'] as CreateTab[]).map(t => (
          <button
            key={t}
            className={`skucreate-tab${tab === t ? ' skucreate-tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {CREATE_TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="skucreate-body">
        {tab === 'general' && (
          <div className="pricelist-edit-form">
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Número de parte *</label>
              <input className="pricelist-edit-input" type="text"
                value={draft.partNumber} onChange={setField('partNumber')} autoFocus />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Nombre del producto</label>
              <input className="pricelist-edit-input" type="text"
                value={draft.skuName} onChange={setField('skuName')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Descripción</label>
              <input className="pricelist-edit-input" type="text"
                value={draft.description} onChange={setField('description')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Tipo de producto</label>
              <input className="pricelist-edit-input" type="text"
                value={draft.skuType} onChange={setField('skuType')} />
            </div>
            {categories.length > 0 && (
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Categoría</label>
                <select className="pricelist-edit-select pricelist-edit-select--country"
                  value={draft.categoryId} onChange={setField('categoryId')}>
                  <option value="">— Seleccionar categoría —</option>
                  {categories.slice().sort((a, b) => {
                    const la = a.type ? `${a.type} > ${a.categoryName}` : (a.categoryName ?? '');
                    const lb = b.type ? `${b.type} > ${b.categoryName}` : (b.categoryName ?? '');
                    return la.localeCompare(lb, 'es');
                  }).map(c => (
                    <option key={String(c.categoryId)} value={String(c.categoryId)}>
                      {c.type ? `${c.type} > ${c.categoryName}` : c.categoryName ?? ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Marca *</label>
              <select className="pricelist-edit-select pricelist-edit-select--country"
                value={draft.brandId} onChange={setField('brandId')}>
                <option value="">— Seleccionar marca —</option>
                {brands.slice().sort((a, b) => a.Name.localeCompare(b.Name, 'es')).map(b => (
                  <option key={b.Id} value={b.Id}>{b.Name}</option>
                ))}
              </select>
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Precio lista (USD) *</label>
              <input className="pricelist-edit-input" type="number"
                step="0.01" min="0.01"
                value={draft.listPrice} onChange={setField('listPrice')} />
            </div>
            <div className="pricelist-edit-field pricelist-edit-field--inline">
              <label className="pricelist-edit-label">Descontinuado</label>
              <input type="checkbox"
                checked={draft.discontinued === 'true'}
                onChange={setCheckboxCreate('discontinued')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Notas</label>
              <textarea className="pricelist-edit-textarea" rows={3}
                value={draft.notes} onChange={setField('notes')} />
            </div>
          </div>
        )}

        {tab === 'shipment' && (
          <div className="pricelist-edit-form">
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Envío exterior (USD)</label>
              <input className="pricelist-edit-input" type="number" step="0.01" min="0"
                value={draft.foreignShipmentCost} onChange={setField('foreignShipmentCost')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Envío nacional (USD)</label>
              <input className="pricelist-edit-input" type="number" step="0.01" min="0"
                value={draft.domesticShipmentCost} onChange={setField('domesticShipmentCost')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Peso (lb)</label>
              <input className="pricelist-edit-input" type="number" step="0.001" min="0"
                value={draft.weight} onChange={setField('weight')} />
            </div>
            <div className="pricelist-edit-field">
              <label className="pricelist-edit-label">Volumen (m³)</label>
              <input className="pricelist-edit-input" type="number" step="0.0001" min="0"
                value={draft.volume} onChange={setField('volume')} />
            </div>
          </div>
        )}

        {tab === 'import' && (
          <div className="pricelist-edit-form">
            <div className="pricelist-journey-edit-group">
              <div className="pricelist-journey-edit-header">Origen</div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">País origen</label>
                <select className="pricelist-edit-select pricelist-edit-select--country"
                  value={draft.countryOrigin} onChange={setField('countryOrigin')}>
                  <option value="">— Sin selección —</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pricelist-journey-edit-group">
              <div className="pricelist-journey-edit-header">País de envío · Impuesto extranjero</div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">País envío</label>
                <select className="pricelist-edit-select pricelist-edit-select--country"
                  value={draft.countryShipment} onChange={setField('countryShipment')}>
                  <option value="">— Sin selección —</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Impuesto extranjero</label>
                <div className="pricelist-cost-row">
                  <select className="pricelist-edit-select" value={draft.foreignTaxType} onChange={setField('foreignTaxType')}>
                    <option value="0">% precio lista</option>
                    <option value="1">% precio c/dto.</option>
                    <option value="2">Monto fijo</option>
                  </select>
                  <input className="pricelist-edit-input" type="number"
                    step={draft.foreignTaxType === '2' ? '0.01' : '0.0001'} min="0"
                    placeholder={draft.foreignTaxType === '2' ? 'USD' : '%'}
                    value={draft.foreignTax} onChange={setField('foreignTax')} />
                </div>
              </div>
            </div>

            <div className="pricelist-journey-edit-group">
              <div className="pricelist-journey-edit-header">Aduana</div>
              {([
                { label: 'Costo aduana 1', valKey: 'customsCosts1', typeKey: 'customsCosts1Type' },
                { label: 'Costo aduana 2', valKey: 'customsCosts2', typeKey: 'customsCosts2Type' },
                { label: 'Costo aduana 3', valKey: 'customsCosts3', typeKey: 'customsCosts3Type' },
              ] as Array<{ label: string; valKey: string; typeKey: string }>).map(({ label, valKey, typeKey }) => (
                <div key={valKey} className="pricelist-edit-field">
                  <label className="pricelist-edit-label">{label}</label>
                  <div className="pricelist-cost-row">
                    <select className="pricelist-edit-select" value={draft[typeKey] ?? '0'} onChange={setField(typeKey)}>
                      <option value="0">% precio lista</option>
                      <option value="1">% precio c/dto.</option>
                      <option value="2">Monto fijo</option>
                    </select>
                    <input className="pricelist-edit-input" type="number"
                      step={draft[typeKey] === '2' ? '0.01' : '0.0001'} min="0"
                      placeholder={draft[typeKey] === '2' ? 'USD' : '%'}
                      value={draft[valKey] ?? ''} onChange={setField(valKey)} />
                  </div>
                </div>
              ))}
            </div>

            <div className="pricelist-journey-edit-group">
              <div className="pricelist-journey-edit-header">Destino · Impuesto nacional</div>
              <div className="pricelist-edit-field">
                <label className="pricelist-edit-label">Impuesto nacional</label>
                <div className="pricelist-cost-row">
                  <select className="pricelist-edit-select" value={draft.nationalTaxType} onChange={setField('nationalTaxType')}>
                    <option value="0">% precio lista</option>
                    <option value="1">% precio c/dto.</option>
                    <option value="2">Monto fijo</option>
                  </select>
                  <input className="pricelist-edit-input" type="number"
                    step={draft.nationalTaxType === '2' ? '0.01' : '0.0001'} min="0"
                    placeholder={draft.nationalTaxType === '2' ? 'USD' : '%'}
                    value={draft.nationalTax} onChange={setField('nationalTax')} />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {saveError && <p className="pricelist-save-error skucreate-save-error">{saveError}</p>}
      <div className="skucreate-actions">
        <button className="pricelist-cancel-btn" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button className="pricelist-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Crear SKU'}
        </button>
      </div>
    </div>
  );
};

// ─── PriceListPriceSearch tab ──────────────────────────────────────

function buildQuoteRows(
  results: SkuDto[],
  simDistrib: boolean,
  simDistribPct: number,
  lockedDiscounts: Record<string, number>
) {
  return results.map((sku, i) => {
    const key = String(sku.skuId ?? sku.partNumber ?? i);
    const effectivePct = simDistrib
      ? (key in lockedDiscounts ? lockedDiscounts[key] : simDistribPct)
      : 0;

    const prices = sku.prices ?? [];
    const defaultIdx = prices.findIndex(p => p.isDefault);
    const fp = prices[defaultIdx >= 0 ? defaultIdx : 0] ?? null;

    const listPrice = Number(fp?.listPrice) || 0;
    const purchaseDiscount = Number(fp?.discount) || 0;
    const purchasePrice = listPrice * (1 - purchaseDiscount);
    const suggestedPrice = Number(fp?.suggestedCustomerPrice) || 0;
    const fixedPrice = sku.fixedPrice != null ? Number(sku.fixedPrice) : null;
    const finalPrice = fixedPrice ?? suggestedPrice;
    const totalCost = Number(fp?.cost) || 0;
    const baseProfit = finalPrice - totalCost;
    const profitPercentage = purchasePrice > 0 ? baseProfit / purchasePrice : 0;
    const distributorDiscount = effectivePct / 100;
    const distributorPrice = finalPrice * (1 - distributorDiscount);
    const finalProfitUsd = distributorPrice - totalCost;
    const finalProfitPercentage = totalCost > 0 ? finalProfitUsd / totalCost : 0;
    const lossUsd = baseProfit - finalProfitUsd;
    const lossPercentage = Math.abs(baseProfit) > 0 ? lossUsd / Math.abs(baseProfit) : 0;

    return {
      partNumber: sku.partNumber,
      name: sku.skuName ?? sku.description ?? null,
      listPrice,
      purchaseDiscount,
      purchasePrice,
      suggestedPrice,
      fixedPrice,
      finalPrice,
      totalCost,
      baseProfit,
      profitPercentage,
      distributorDiscount,
      distributorPrice,
      finalProfitUsd,
      finalProfitPercentage,
      lossUsd,
      lossPercentage,
    };
  });
}

const PriceListPriceSearch: React.FC<{ pendingSearch: { pn: string } | null }> = ({ pendingSearch }) => {
  const entitlements = useSkuEntitlements();
  const [value, setValue] = useState('');
  const [results, setResults] = useState<SkuDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [simDistrib, setSimDistrib] = useState(false);
  const [simDistribPct, setSimDistribPct] = useState(20);
  const [lockedDiscounts, setLockedDiscounts] = useState<Record<string, number>>({});
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (pendingSearch) {
      setValue(pendingSearch.pn);
      setResults(null);
      setError(null);
    }
  }, [pendingSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResults(null);
    setLoading(true);
    await searchSkuByPartNumber(value, {
      onSuccess: (data) => setResults(data),
      onNotFound: () => setError('No se encontraron resultados'),
      onBadRequest: (msg) => setError(msg),
      onError: (msg) => setError(msg),
    });
    setLoading(false);
  };

  const handleCreated = (sku: SkuDto) => {
    setShowCreate(false);
    setResults(prev => prev ? [sku, ...prev] : [sku]);
    setError(null);
  };

  const handleExport = async () => {
    if (!results || results.length === 0) return;
    setExporting(true);
    setExportError(null);
    const rows = buildQuoteRows(results, simDistrib, simDistribPct, lockedDiscounts);
    await exportQuote(rows, (msg) => setExportError(msg));
    setExporting(false);
  };

  return (
    <>
      <div className="bp-toolbar">
        <form className="bp-toolbar-form" onSubmit={handleSubmit}>
          <div className="bp-searchbar">
            <span className="bp-searchbar-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
            </span>
            <input
              className="bp-searchbar-input"
              id="txt-partnumber"
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="Número de parte…"
              disabled={loading}
              autoComplete="off"
              spellCheck={false}
            />
            {value && (
              <button className="bp-searchbar-clear" type="button" onClick={() => setValue('')} aria-label="Limpiar búsqueda">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            )}
          </div>
          <button type="submit" className="pricelist-save-btn bp-toolbar-submit" disabled={loading || !value.trim()}>
            {loading ? 'Buscando…' : 'Buscar'}
          </button>
        </form>
        {can.addSku(entitlements) && (
          <button
            className={`skucreate-new-btn${showCreate ? ' skucreate-new-btn--active' : ''}`}
            onClick={() => setShowCreate(s => !s)}
          >
            {showCreate ? '✕ Cancelar' : '+ Nuevo SKU'}
          </button>
        )}
      </div>
      {showCreate && (
        <SkuCreateForm
          onSaved={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}
      {error && <p className="pricelist-error">{error}</p>}
      {results && (
        <>
          <div className="pricelist-results-header">
            <span className="pricelist-results-count">{results.length} resultado{results.length !== 1 ? 's' : ''}</span>
            <div className="pricelist-results-actions">
              {can.exportQuote(entitlements) && (
                <button
                  className="pricelist-export-btn"
                  onClick={handleExport}
                  disabled={exporting}
                  title="Exportar cotización a Excel"
                >
                  {exporting ? 'Exportando…' : 'Exportar'}
                </button>
              )}
              <button className="pricelist-results-clear" onClick={() => { setResults(null); setError(null); setSimDistrib(false); setLockedDiscounts({}); setExportError(null); }}>
                Limpiar resultados
              </button>
            </div>
          </div>
          {exportError && <p className="pricelist-error">{exportError}</p>}
          {can.simulateCustomerDiscount(entitlements) && (
            <div className="pricelist-sim-bar">
              <label className="pricelist-sim-label">
                <input
                  type="checkbox"
                  checked={simDistrib}
                  onChange={e => { setSimDistrib(e.target.checked); if (!e.target.checked) setLockedDiscounts({}); }}
                />
                Simular descuento a distribuidor
              </label>
              {simDistrib && (() => {
                const simTotals = results.reduce((acc, sku, i) => {
                  const key = String(sku.skuId ?? sku.partNumber ?? i);
                  const effectivePct = key in lockedDiscounts ? lockedDiscounts[key] : simDistribPct;
                  const prices = sku.prices ?? [];
                  const defaultIdx = prices.findIndex(p => p.isDefault);
                  const fp0 = prices[defaultIdx >= 0 ? defaultIdx : 0];
                  const baseSell = sku.fixedPrice != null ? Number(sku.fixedPrice) : Number(fp0?.suggestedCustomerPrice) || 0;
                  const cost = Number(fp0?.cost) || 0;
                  const baseProfit = baseSell - cost;
                  const distribSell = baseSell * (1 - effectivePct / 100);
                  const profitAfterDiscount = distribSell - cost;
                  return {
                    drop: acc.drop + (baseProfit - profitAfterDiscount),
                    profit: acc.profit + profitAfterDiscount,
                  };
                }, { drop: 0, profit: 0 });
                return (
                  <>
                    <div className="pricelist-sim-slider-row">
                      <span className="pricelist-edit-label">Descuento</span>
                      <input
                        className="pricelist-distrib-slider"
                        type="range" step="1" min="0" max="50"
                        value={simDistribPct}
                        onChange={e => setSimDistribPct(Number(e.target.value))}
                      />
                      <span className="pricelist-sim-pct-label">{simDistribPct}%</span>
                      <span className="pricelist-distrib-hint">Simulación — no se guarda</span>
                    </div>
                    <div className="pricelist-sim-total-drop">
                      <span className="pricelist-sim-total-drop-label">Pérdida de utilidad acumulada</span>
                      <span className="pricelist-sim-total-drop-value">↓ {fmtCurrency(simTotals.drop)}</span>
                      <span className="pricelist-sim-total-drop-sep" aria-hidden="true">·</span>
                      <span className="pricelist-sim-total-drop-label">Utilidad total</span>
                      <span className={`pricelist-sim-total-drop-value ${simTotals.profit < 0 ? 'pricelist-sim-loss' : 'pricelist-sim-profit'}`}>{fmtCurrency(simTotals.profit)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          <div className="pricelist-cards">
            {results.map((sku, i) => {
              const key = String(sku.skuId ?? sku.partNumber ?? i);
              const isLocked = key in lockedDiscounts;
              const effectivePct = isLocked ? lockedDiscounts[key] : simDistribPct;
              return (
                <SkuCard
                  key={i}
                  sku={sku}
                  simDistribPct={simDistrib ? effectivePct : undefined}
                  simLocked={simDistrib && isLocked}
                  onToggleLock={simDistrib ? () => setLockedDiscounts(prev => {
                    if (key in prev) { const next = { ...prev }; delete next[key]; return next; }
                    return { ...prev, [key]: simDistribPct };
                  }) : undefined}
                />
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default PriceListPriceSearch;
